/**
 * Formato y validación de RNC y cédula — una sola fuente.
 *
 * Regla del documento: **con guiones al mostrar, sin guiones al enviar.**
 * El backend y DGII reciben siempre los dígitos pelados.
 *
 * Dos longitudes válidas:
 *   9 dígitos  → RNC de empresa   `131880738`   → `1-31-88073-8`
 *   11 dígitos → cédula (persona) `00100000009` → `001-0000000-9`
 */

export type TipoDocumentoFiscal = 'rnc' | 'cedula' | 'desconocido'

/** Deja solo dígitos. Es lo que viaja al servidor. */
export function limpiarRNC(valor: string | null | undefined): string {
  if (!valor) return ''
  return String(valor).replace(/\D/g, '')
}

/** 9 dígitos es RNC, 11 es cédula, cualquier otra cosa es desconocido. */
export function tipoDocumentoFiscal(valor: string | null | undefined): TipoDocumentoFiscal {
  const digitos = limpiarRNC(valor)
  if (digitos.length === 9) return 'rnc'
  if (digitos.length === 11) return 'cedula'
  return 'desconocido'
}

/**
 * Aplica los guiones de presentación.
 * Si el valor no tiene 9 ni 11 dígitos se devuelve tal cual: es preferible
 * mostrar un dato raro a inventarle un formato que oculte el error.
 */
export function formatearRNC(valor: string | null | undefined): string {
  const digitos = limpiarRNC(valor)

  if (digitos.length === 9) {
    return `${digitos.slice(0, 1)}-${digitos.slice(1, 3)}-${digitos.slice(3, 8)}-${digitos.slice(8, 9)}`
  }

  if (digitos.length === 11) {
    return `${digitos.slice(0, 3)}-${digitos.slice(3, 10)}-${digitos.slice(10, 11)}`
  }

  return valor ? String(valor) : ''
}

/**
 * Validación estructural: 9 u 11 dígitos.
 *
 * Decisión deliberada: **el dígito verificador no bloquea la entrada.** Se
 * calcula aparte (`digitoVerificadorCorrecto`) y se usa como aviso, no como
 * error duro. Rechazar en el cliente un RNC que DGII sí conoce manda al usuario
 * a soporte con un número que sirve; la verdad sobre un RNC la tiene el
 * directorio de DGII (`GET /interna/contrapartes/{rnc}`), no una fórmula local.
 */
export function esRNCValido(valor: string | null | undefined): boolean {
  return tipoDocumentoFiscal(valor) !== 'desconocido'
}

/**
 * Dígito verificador del RNC de 9 dígitos (módulo 11, pesos 7-9-8-6-5-4-3-2).
 * Devuelve `null` cuando no aplica (no son 9 dígitos).
 *
 * Solo para avisar "revisa este número", nunca para rechazarlo.
 */
export function digitoVerificadorCorrecto(valor: string | null | undefined): boolean | null {
  const digitos = limpiarRNC(valor)
  if (digitos.length !== 9) return null

  const pesos = [7, 9, 8, 6, 5, 4, 3, 2]
  let suma = 0
  for (let i = 0; i < 8; i += 1) {
    suma += Number(digitos[i]) * pesos[i]
  }

  const resto = suma % 11
  const esperado = resto === 0 ? 2 : resto === 1 ? 1 : 11 - resto
  return esperado === Number(digitos[8])
}

/**
 * Máscara progresiva para teclear. Se llama en cada pulsación del campo y
 * devuelve lo que debe verse: `1318` → `1-31-8`.
 * Nunca recorta más de 11 dígitos.
 */
export function enmascararMientrasEscribe(valor: string): string {
  const digitos = limpiarRNC(valor).slice(0, 11)
  if (digitos.length === 0) return ''

  if (digitos.length <= 9) {
    const partes = [digitos.slice(0, 1), digitos.slice(1, 3), digitos.slice(3, 8), digitos.slice(8, 9)]
    return partes.filter((parte) => parte !== '').join('-')
  }

  const partes = [digitos.slice(0, 3), digitos.slice(3, 10), digitos.slice(10, 11)]
  return partes.filter((parte) => parte !== '').join('-')
}
